import { StyleSheet, View } from "react-native";
import PressableScale from "@shared/components/md-design-system/Button/PressableScaleButton";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { Text } from "@shared/components/text";
import { colorPalette } from "@shared/config/colorsConfig";
import { typography } from "@shared/config/typographyConfig";
import { TourAnchor } from "app/_modules/onboarding";
import type { TTourAnchor } from "app/_modules/onboarding";
import { HOME_CARD } from "../../../constants";
import { ActivityIconBox } from "./ActivityIconBox";
import { ActivityCtaButton } from "./ActivityCtaButton";

const styles = StyleSheet.create({
    ctaAnchor: { alignSelf: 'center' },
});

interface IActivityActionCardProps {
    activityType: string;
    activityName: string;
    title: string;
    whenText?: string;
    urgentNote?: string;
    showClockIcon?: boolean;
    value?: string;
    actionLabel: string;
    onPress: () => void;
    disabled?: boolean;
    variant?: 'default' | 'warning';
    blockAction?: boolean;
    ctaAnchor?: TTourAnchor;
}

export const ActivityActionCard = ({
    activityType,
    activityName,
    title,
    whenText,
    urgentNote,
    showClockIcon = false,
    value,
    actionLabel,
    onPress,
    disabled = false,
    variant = 'default',
    blockAction = false,
    ctaAnchor,
}: IActivityActionCardProps) => {
    const isWarning = variant === 'warning';

    const content = (
        <View className="flex-col">
            <View className="flex-row items-center gap-4">
                <ActivityIconBox
                    activityType={activityType}
                    activityName={activityName}
                    size={HOME_CARD.iconBox}
                    glyphSize={HOME_CARD.iconGlyph}
                    color={isWarning ? colorPalette.black : colorPalette.secondary[600]}
                />

                <View className="flex-1">
                    <Text className="font-bold text-base" numberOfLines={2}>{title}</Text>

                    {!!value && (
                        <Text
                            className="font-heading font-bold text-black mt-1 mb-0.5"
                            style={{ fontSize: typography.fontSize['3xl'], lineHeight: typography.lineHeight['3xl'] }}
                        >
                            {value}
                        </Text>
                    )}

                    {!!whenText && (
                        <View className="flex-row items-center gap-2 mt-2">
                            {showClockIcon && <Icon name="clock-circle" size={17} color={colorPalette.gray[500]} />}
                            <Text className={`text-md text-gray-500 ${isWarning ? 'font-semibold' : ''}`}>
                                {whenText}
                                {!!urgentNote && ' · '}
                                {!!urgentNote && <Text className="text-md font-bold text-gray-500">{urgentNote}</Text>}
                            </Text>
                        </View>
                    )}
                </View>

                {!blockAction && (
                    <TourAnchor anchor={ctaAnchor} fill={false} style={styles.ctaAnchor}>
                        <ActivityCtaButton onPress={onPress} disabled={disabled}>
                            {actionLabel}
                        </ActivityCtaButton>
                    </TourAnchor>
                )}
            </View>

            {blockAction && (
                <TourAnchor anchor={ctaAnchor}>
                    <ActivityCtaButton onPress={onPress} disabled={disabled} block>
                        {actionLabel}
                    </ActivityCtaButton>
                </TourAnchor>
            )}
        </View>
    );

    if (isWarning) {
        return (
            <View
                className="rounded-2xl px-6 py-4 bg-primary-100 border-[1.5px] border-primary-200"
                style={HOME_CARD.shadow}
                renderToHardwareTextureAndroid
                shouldRasterizeIOS
            >
                {content}
            </View>
        );
    }

    return (
        <View className="rounded-2xl" style={HOME_CARD.shadow} renderToHardwareTextureAndroid shouldRasterizeIOS>
            <PressableScale
                onPress={onPress}
                disabled={disabled}
                rippleColor={colorPalette.primary[100]}
                pressedTintColor={colorPalette.primary[100]}
                frameClassName="border border-gray-300 bg-white"
                pressedFrameClassName="border-primary-700"
                contentClassName="px-6 py-4 rounded-2xl"
            >
                {content}
            </PressableScale>
        </View>
    );
};

export default ActivityActionCard;
